import React, { JSX, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';

import { Box, Button, Container } from '@mui/material';

import { getSentEmail } from '~/services/SailthruService';

function getWrappedHtml(html: string): string {
  // Some email content does not import a font family, which can render unreadable text when printing.
  const fallbackFontLink = `<link href="https://fonts.googleapis.com/css?family=IBM+Plex+Sans:400,600" rel="stylesheet">`;

  return `
    ${fallbackFontLink}
    <div style="max-width: 75%; margin: 0 auto; font-family: 'IBM Plex Sans', Arial, Helvetica, sans-serif;">
      ${html}
    </div>`;
}

function RenderEmailPage(): JSX.Element {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');
    if (!id) {
      return;
    }

    getSentEmail({ id }).then((data) => setHtmlContent(data));
  }, []);

  function handlePrint() {
    const printWindow = iframeRef.current?.contentWindow;
    if (!printWindow) {
      enqueueSnackbar('Unable to open the print dialog', { variant: 'error' });
      return;
    }

    // The email is rendered in an isolated iframe, so printing it opens the native
    // print dialog (where the user can "Save as PDF") with only the email content.
    printWindow.focus();
    printWindow.print();
  }

  const title = `View Email`;
  return (
    <Container>
      <Container maxWidth="xl">
        <Button variant="contained" onClick={handlePrint} disabled={!htmlContent} sx={{ mt: 2 }}>
          Print / Save as PDF
        </Button>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <Box
          component="iframe"
          ref={iframeRef}
          title={title}
          srcDoc={getWrappedHtml(htmlContent)}
          // Isolate the untrusted email markup: same-origin so we can trigger print(),
          // modals so the print dialog is allowed, but no allow-scripts so embedded scripts cannot run.
          sandbox="allow-same-origin allow-modals"
          sx={{ mt: 2, width: '100%', height: '80vh', border: 'none' }}
        />
      </Container>
    </Container>
  );
}

export default RenderEmailPage;
